<template>
  <div class="card" v-loading="loading">
    <div class="card-content">
      <div class="msub-cal__head">
        <div class="msub-cal__nav">
          <a class="btn btn-sm btn-default" @click="changeMonth(-1)"><i class="fa fa-chevron-left"></i></a>
          <strong class="msub-cal__month">{{ monthLabel }}</strong>
          <a class="btn btn-sm btn-default" @click="changeMonth(1)"><i class="fa fa-chevron-right"></i></a>
        </div>
        <div class="msub-cal__legend">
          <span><i class="msub-cal__dot msub-cal__dot--custom"></i> {{ $t('menuSubscriptions.calendarCustom') }}</span>
          <span><i class="msub-cal__dot msub-cal__dot--default"></i> {{ $t('menuSubscriptions.calendarDefault') }}</span>
          <span><i class="msub-cal__dot msub-cal__dot--off"></i> {{ $t('menuSubscriptions.calendarNoDelivery') }}</span>
        </div>
      </div>
      <small class="text-muted msub-block">{{ $t('menuSubscriptions.calendarHint') }}</small>

      <p v-if="!menus.length && !loading" class="msub-cal__warn">
        <i class="fa fa-exclamation-triangle"></i> {{ $t('menuSubscriptions.calendarNoMenus') }}
      </p>

      <div class="msub-cal">
        <div v-for="label in weekLabels" :key="'wl' + label" class="msub-cal__weekday">{{ label }}</div>
        <div v-for="cell in cells"
             :key="cell.date"
             class="msub-cal__cell"
             :class="{
               'is-other-month': !cell.inMonth,
               'is-past': cell.past,
               'is-off': !cell.delivery,
               'is-selected': isSelected(cell.date),
               'is-today': cell.date === today,
               'is-selectable': cell.selectable
             }"
             @click="toggle(cell)">
          <div class="msub-cal__day">{{ cell.day }}</div>
          <template v-if="cell.delivery">
            <div v-if="cell.menuId && menuName(cell.menuId)" class="msub-cal__menu msub-cal__menu--custom">{{ menuName(cell.menuId) }}</div>
            <div v-else-if="defaultMenu" class="msub-cal__menu msub-cal__menu--default">{{ defaultMenu.name }}</div>
            <div v-else class="msub-cal__menu msub-cal__menu--none">{{ $t('menuSubscriptions.calendarNoMenu') }}</div>
          </template>
          <div v-else class="msub-cal__menu msub-cal__menu--off">
            {{ cell.menuId && menuName(cell.menuId) ? menuName(cell.menuId) + ' · ' : '' }}{{ $t('menuSubscriptions.calendarNoDeliveryShort') }}
          </div>
        </div>
      </div>

      <div class="msub-cal__select-tools">
        <a class="btn btn-sm btn-default" @click="selectMonth"><i class="fa fa-check-square-o"></i> {{ $t('menuSubscriptions.selectMonth') }}</a>
        <a v-if="selected.length" class="btn btn-sm btn-default" @click="selected = []"><i class="fa fa-square-o"></i> {{ $t('menuSubscriptions.clearSelection') }}</a>
      </div>

      <!-- actions for the selected days -->
      <div v-if="selected.length" class="msub-cal__actions">
        <div class="msub-cal__actions-title">{{ $t('menuSubscriptions.selectedDays', { count: selected.length }) }}</div>
        <div class="msub-cal__actions-row">
          <el-select v-model="assignMenuId" filterable :placeholder="$t('menuSubscriptions.chooseMenu')">
            <el-option v-for="menu in menus" :key="'am' + menu.id" :label="menu.name + (menu.is_default ? ' (' + $t('menuSubscriptions.defaultMenu') + ')' : '')" :value="menu.id"></el-option>
          </el-select>
          <button class="btn btn-sm btn-purple-light btn-fill" :disabled="!assignMenuId || saving" @click="assign(assignMenuId)">{{ $t('menuSubscriptions.applyMenu') }}</button>
          <button class="btn btn-sm btn-default" :disabled="saving" @click="assign(null)">{{ $t('menuSubscriptions.resetToDefault') }}</button>
          <button class="btn btn-sm btn-purple" :disabled="saving" @click="$emit('new-menu-for-dates', selected)"><i class="fa fa-plus"></i> {{ $t('menuSubscriptions.newMenuForDays') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {Select, Option} from 'element-ui'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'
  import { DAY_NAMES } from './menuSubscriptionHelpers'

  Vue.use(Select)
  Vue.use(Option)

  function pad (number) {
    return String(number).padStart(2, '0')
  }

  function ymd (date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
  }

  // JS getDay(): 0 = sunday -> DAY_NAMES index (0 = monday)
  function dayName (date) {
    return DAY_NAMES[(date.getDay() + 6) % 7]
  }

  export default {
    props: {
      subscription: {
        type: Object,
        required: true
      }
    },
    data () {
      const now = new Date()
      return {
        loading: false,
        saving: false,
        year: now.getFullYear(),
        month: now.getMonth(),
        // today in the company timezone (from the API)
        today: ymd(now),
        menus: [],
        // date => menu id (only the days set in the calendar)
        assigned: {},
        selected: [],
        assignMenuId: null
      }
    },
    computed: {
      weekLabels () {
        return DAY_NAMES.map(day => this.$t('menuSubscriptions.dayShort.' + day))
      },
      monthLabel () {
        const label = this.$t('menuSubscriptions.months.' + this.month)
        return label + ' ' + this.year
      },
      defaultMenu () {
        return this.menus.find(menu => menu.is_default) || null
      },
      // 6 weeks grid starting on monday
      cells () {
        const first = new Date(this.year, this.month, 1)
        const start = new Date(this.year, this.month, 1 - ((first.getDay() + 6) % 7))
        const deliveryDays = this.subscription.delivery_days || []
        const cells = []
        for (let i = 0; i < 42; i++) {
          const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
          const key = ymd(date)
          const delivery = deliveryDays.indexOf(dayName(date)) !== -1
          const past = key < this.today
          cells.push({
            date: key,
            day: date.getDate(),
            inMonth: date.getMonth() === this.month,
            delivery,
            past,
            selectable: delivery && !past,
            menuId: this.assigned[key] || null
          })
        }
        return cells
      },
      menusById () {
        const map = {}
        this.menus.forEach(menu => {
          map[menu.id] = menu
        })
        return map
      }
    },
    created () {
      this.getMenus()
      this.getCalendar()
    },
    methods: {
      requestData () {
        return {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
      },
      url (path) {
        return '/menu-subscription/' + this.subscription.id + path
      },
      getMenus () {
        this.apiRequest(this.url('/menus'), 'GET', this.requestData(), (response) => {
          if (response.status === true && response.data) {
            this.menus = response.data.menus || []
          }
        })
      },
      getCalendar () {
        const cells = this.cells
        const data = {
          ...this.requestData(),
          from: cells[0].date,
          to: cells[cells.length - 1].date
        }
        this.loading = true
        this.apiRequest(this.url('/calendar'), 'GET', data, (response) => {
          this.loading = false
          if (response.status !== true || !response.data) {
            return
          }
          const assigned = {}
          ;(response.data.days || []).forEach(day => {
            assigned[day.date] = day.menu_id
          })
          this.assigned = assigned
          if (response.data.today) {
            this.today = response.data.today
          }
        })
      },
      changeMonth (step) {
        const date = new Date(this.year, this.month + step, 1)
        this.year = date.getFullYear()
        this.month = date.getMonth()
        this.selected = []
        this.getCalendar()
      },
      menuName (id) {
        return this.menusById[id] ? this.menusById[id].name : null
      },
      isSelected (date) {
        return this.selected.indexOf(date) !== -1
      },
      toggle (cell) {
        if (!cell.selectable) {
          return
        }
        const index = this.selected.indexOf(cell.date)
        if (index === -1) {
          this.selected.push(cell.date)
        } else {
          this.selected.splice(index, 1)
        }
      },
      selectMonth () {
        this.selected = this.cells.filter(cell => cell.inMonth && cell.selectable).map(cell => cell.date)
      },
      assign (menuId) {
        const data = {
          ...this.requestData(),
          dates: this.selected.slice().sort(),
          menu_id: menuId
        }
        this.saving = true
        this.apiRequest(this.url('/calendar'), 'PUT', data, (response) => {
          this.saving = false
          if (response.status === true) {
            this.selected = []
            this.assignMenuId = null
            this.getCalendar()
            swal({ title: '', text: this.$t('menuSubscriptions.calendarSaved'), type: 'success', showConfirmButton: false, timer: 1200 }).catch(() => {})
          } else {
            const errors = response.errors || {}
            const key = Object.keys(errors)[0]
            swal('', key ? errors[key] : this.$t('validations.wentWrong'), 'error').catch(() => {})
          }
        })
      }
    }
  }
</script>
<style>
  .msub-cal__head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }
  .msub-cal__nav {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .msub-cal__month {
    min-width: 150px;
    text-align: center;
    font-size: 16px;
  }
  .msub-cal__legend {
    display: flex;
    gap: 14px;
    font-size: 12px;
    color: #777;
  }
  .msub-cal__dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 3px;
    margin-right: 4px;
  }
  .msub-cal__dot--custom {
    background: #7e57c2;
  }
  .msub-cal__dot--default {
    background: #d1c4e9;
  }
  .msub-cal__dot--off {
    background: #eee;
  }
  .msub-cal__warn {
    margin-top: 10px;
    color: #c77700;
  }
  .msub-cal {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    margin-top: 12px;
  }
  .msub-cal__weekday {
    text-align: center;
    font-size: 12px;
    color: #999;
    padding: 4px 0;
  }
  .msub-cal__cell {
    min-height: 74px;
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 4px 6px;
    font-size: 12px;
    background: #fff;
    overflow: hidden;
  }
  .msub-cal__cell.is-selectable {
    cursor: pointer;
  }
  .msub-cal__cell.is-selectable:hover {
    border-color: #b39ddb;
  }
  .msub-cal__cell.is-other-month {
    opacity: 0.45;
  }
  .msub-cal__cell.is-off {
    background: #fafafa;
  }
  .msub-cal__cell.is-past {
    background: #f5f5f5;
    color: #aaa;
  }
  .msub-cal__cell.is-today {
    border-color: #7e57c2;
  }
  .msub-cal__cell.is-selected {
    border: 2px solid #5e35b1;
    background: #ede7f6;
  }
  .msub-cal__day {
    font-weight: 600;
    margin-bottom: 4px;
  }
  .msub-cal__menu {
    border-radius: 4px;
    padding: 2px 4px;
    line-height: 1.3;
    word-break: break-word;
  }
  .msub-cal__menu--custom {
    background: #7e57c2;
    color: #fff;
  }
  .msub-cal__menu--default {
    background: #ede7f6;
    color: #5e35b1;
  }
  .msub-cal__menu--none {
    color: #c77700;
  }
  .msub-cal__menu--off {
    color: #bbb;
  }
  .msub-cal__select-tools {
    display: flex;
    gap: 8px;
    margin-top: 10px;
  }
  .msub-cal__actions {
    margin-top: 12px;
    padding: 10px 12px;
    border: 1px solid #d1c4e9;
    border-radius: 6px;
    background: #faf8fe;
  }
  .msub-cal__actions-title {
    font-weight: 600;
    margin-bottom: 8px;
  }
  .msub-cal__actions-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  .msub-cal__actions-row .el-select {
    min-width: 240px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/MenuSubscriptions/MenuSubscriptionCalendar.vue